import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Warning from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { VerifierFragmentFragment, Issue } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import pluralize from "pluralize";
import { useMemo, useState, useCallback } from "react";
import { useBulkVerifyInputValidation } from "routes/Staff/Fulfillment/validationHooks/useBulkVerifyInputValidation";

import { IssueListButton } from "./Forms/IssueListButton";
import { VerifierTable } from "./VerifierTable";
import {
  defaultVerifiedAtDate,
  ReportForDefaultDate,
  signatureDateConflict,
} from "./verifyAndSubmitDefaultDate";

export type VerifyAndSubmitFormState = {
  personId?: string;
  verifierFullName: string;
  verifierTitle: string;
  verifierPhone: string;
  verifierEmail: string;
  date: string;
};

type BaseProps = {
  onClose: () => void;
  open: boolean;
  loading: boolean;
  defaultVerifier?: {
    person?: VerifierFragmentFragment;
    fullName?: string;
    title?: string;
  };
  /** Reports whose signature-date default is being computed; see verifyAndSubmitDefaultDate. */
  reports: ReportForDefaultDate[];
  reportIds?: string[];
};

type Props = BaseProps &
  (
    | {
        onSubmit: (formState: VerifyAndSubmitFormState) => Promise<void>;
        withConfirmation?: false;
      }
    | {
        onSubmit: (
          formState: VerifyAndSubmitFormState,
          overrideOpenReviews: boolean
        ) => Promise<void>;
        withConfirmation: true;
        skippedDueToOpenReviewsCount: number;
        skippedDueToVerificationCount: number;
        verifiableRowsWithoutOpenReviewsCount: number;
        reportsWithValidationErrorsCount: number;
        /** Reports submitted when the open-review safety check is overridden; defaults to `reports`. */
        overrideReports?: ReportForDefaultDate[];
      }
  );

export const VerifyAndSubmitFormDialog = (props: Props) => {
  const theme = useTheme();
  const validateInput = useBulkVerifyInputValidation();

  const overrideReports =
    (props.withConfirmation ? props.overrideReports : undefined) ?? props.reports;
  // With nothing submittable by default, only the override set can be sent.
  const defaultDate = useMemo(
    () => defaultVerifiedAtDate(props.reports.length > 0 ? props.reports : overrideReports),
    [props.reports, overrideReports]
  );

  const [person, setPerson] = useState<VerifierFragmentFragment | null>(
    props.defaultVerifier?.person ?? null
  );

  const [showConfirmation, setShowConfirmation] = useState(false);

  // Create validator function for useValidatingForm
  const validator = useCallback(
    async (formData: VerifyAndSubmitFormState): Promise<Issue[]> => {
      if (!props.reportIds || props.reportIds.length === 0) {
        return [];
      }

      const verifierData = {
        verifiedAt: formData.date,
        verifierFullName: formData.verifierFullName,
        verifierTitle: formData.verifierTitle,
        verifierEmail: formData.verifierEmail,
        verifierPhone: formData.verifierPhone,
      };

      return await validateInput(props.reportIds, verifierData);
    },
    [props.reportIds, validateInput]
  );

  const defaultFormData = useMemo(
    () => ({
      personId: person?.id ?? undefined,
      verifierFullName:
        person?.first && person?.last
          ? `${person?.first} ${person?.last}`
          : (props.defaultVerifier?.fullName ?? ""),
      verifierTitle: person?.title ?? props.defaultVerifier?.title ?? "",
      verifierPhone: person?.phones?.[0]?.number ?? "",
      verifierEmail: person?.email ?? "",
      date: defaultDate,
    }),
    [person, props.defaultVerifier, defaultDate]
  );

  const form = useValidatingForm(defaultFormData, undefined, validator);
  const date = form.watch("date");
  const dateConflict = useMemo(
    () => signatureDateConflict(props.reports, date),
    [props.reports, date]
  );
  const dateConflictWithOverride = useMemo(
    () => signatureDateConflict(overrideReports, date),
    [overrideReports, date]
  );

  const handleClose = () => {
    if (props.withConfirmation) {
      setShowConfirmation(false);
    }
    props.onClose();
    setPerson(null);
    form.reset();
  };

  const onVerify = async (formState: VerifyAndSubmitFormState) => {
    if (props.withConfirmation) {
      setShowConfirmation(true);
    } else {
      await props.onSubmit(formState);
      handleClose();
    }
  };
  const onClearPerson = () => {
    setPerson(null);
    form.reset(defaultFormData);
  };

  const onSelectPerson = (person: VerifierFragmentFragment | null) => {
    setPerson(person);
    form.setValue("personId", person?.id);
    form.setValue(
      "verifierFullName",
      person?.first && person?.last ? `${person?.first} ${person?.last}` : ""
    );
    form.setValue("verifierTitle", person?.title ?? "");
    form.setValue("verifierPhone", person?.phones?.[0]?.number ?? "");
    form.setValue("verifierEmail", person?.email ?? "");
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="lg"
      title={
        <Box display="flex">
          Verify & Submit
          <Tooltip title="Use the verifier table to find a verifier, OR enter one manually">
            <IconButton size="small" style={{ marginLeft: theme.spacing(1) }}>
              <InfoOutlined fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      }
      primaryActionButtonProps={{
        onClick: form.handleSubmit(onVerify),
        disabled: form.formState.isSubmitting || form.issues.length > 0 || dateConflict !== null,
        loading: props.loading,
        saveText: "Verify & Submit",
      }}
      additionalPrimaryActionElements={[
        <>{form.issues.length > 0 && <IssueListButton issues={form.issues} />}</>,
        <Button key="clear-person" onClick={onClearPerson} variant="outlined">
          Clear Person
        </Button>,
      ]}
    >
      {dateConflict && <SignatureDateAlert message={dateConflict} />}
      <Grid container spacing={1}>
        <Grid item xs={12} md={6} lg={4.8}>
          <FormTextField name="verifierFullName" label="Full Name" control={form.control} />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <FormTextField name="verifierTitle" label="Title" control={form.control} />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <FormTextField name="verifierPhone" label="Phone" control={form.control} />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <FormTextField name="verifierEmail" label="Email" control={form.control} />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <DateField name="date" label="Verified On" control={form.control} />
        </Grid>
      </Grid>

      <VerifierTable onSelectPerson={onSelectPerson} />

      {props.withConfirmation && showConfirmation && (
        <ConfirmationDialog
          loading={props.loading}
          onClose={() => setShowConfirmation(false)}
          onSubmit={(overrideOpenReviews) =>
            form.handleSubmit(async (formState) => {
              await props.onSubmit(formState, overrideOpenReviews);
              handleClose();
            })()
          }
          skippedDueToOpenReviewsCount={props.skippedDueToOpenReviewsCount}
          skippedDueToVerificationCount={props.skippedDueToVerificationCount}
          verifiableRowsWithoutOpenReviewsCount={props.verifiableRowsWithoutOpenReviewsCount}
          reportsWithValidationErrorsCount={props.reportsWithValidationErrorsCount}
          dateConflict={dateConflict}
          dateConflictWithOverride={dateConflictWithOverride}
        />
      )}
    </Dialog>
  );
};

const SignatureDateAlert = ({ message }: { message: string }) => (
  <Alert severity="error" sx={{ mb: 1 }}>
    {message}
  </Alert>
);

type ConfirmationProps = {
  loading: boolean;
  onClose: () => void;
  onSubmit: (overrideOpenReviews: boolean) => Promise<void>;
  skippedDueToOpenReviewsCount: number;
  skippedDueToVerificationCount: number;
  verifiableRowsWithoutOpenReviewsCount: number;
  reportsWithValidationErrorsCount: number;
  dateConflict: string | null;
  dateConflictWithOverride: string | null;
};

const ConfirmationDialog = (props: ConfirmationProps) => {
  const [overrideOpenReviews, setOverrideOpenReviews] = useState(false);
  const conflict = overrideOpenReviews ? props.dateConflictWithOverride : props.dateConflict;

  const aboutToVerifyCount =
    props.verifiableRowsWithoutOpenReviewsCount +
    (overrideOpenReviews ? props.skippedDueToOpenReviewsCount : 0);

  return (
    <Dialog
      open
      onClose={props.onClose}
      title="Are you sure?"
      primaryActionButtonProps={{
        loading: props.loading,
        disabled: aboutToVerifyCount === 0 || conflict !== null,
        onClick: () => props.onSubmit(overrideOpenReviews),
        saveText: "Yes, I'm sure",
      }}
    >
      {conflict && <SignatureDateAlert message={conflict} />}
      <p>
        You are about to verify <b>{aboutToVerifyCount}</b>{" "}
        {pluralize("report", aboutToVerifyCount)}.
      </p>
      {!!props.skippedDueToVerificationCount && (
        <p>
          <b>{props.skippedDueToVerificationCount}</b>{" "}
          {pluralize("report", props.skippedDueToVerificationCount)}{" "}
          {pluralize("was", props.skippedDueToVerificationCount)} skipped because{" "}
          {props.skippedDueToVerificationCount > 1 ? "they" : "it"}{" "}
          {props.skippedDueToVerificationCount > 1 ? "are" : "is"} not awaiting verification.
        </p>
      )}
      {!!props.skippedDueToOpenReviewsCount && (
        <>
          {!overrideOpenReviews && (
            <p>
              <b>{props.skippedDueToOpenReviewsCount}</b>{" "}
              {pluralize("report", props.skippedDueToOpenReviewsCount)}{" "}
              {pluralize("was", props.skippedDueToOpenReviewsCount)} skipped because{" "}
              {props.skippedDueToOpenReviewsCount > 1 ? "they" : "it"}{" "}
              {props.skippedDueToOpenReviewsCount > 1 ? "have" : "has"} pending reviews.
            </p>
          )}
          <FormControlLabel
            sx={{ marginLeft: "0px" }}
            control={
              <Checkbox
                sx={{ padding: "9px 4px 9px 0px" }}
                checked={overrideOpenReviews}
                onChange={() => setOverrideOpenReviews(!overrideOpenReviews)}
              />
            }
            label={
              <Typography fontSize={14} fontWeight={500}>
                I want to override this safety check and include{" "}
                {props.skippedDueToOpenReviewsCount}{" "}
                {pluralize("report", props.skippedDueToOpenReviewsCount)} with pending reviews.
              </Typography>
            }
          />
        </>
      )}

      {props.reportsWithValidationErrorsCount > 0 && (
        <Stack direction="row" gap={1}>
          <Warning color="error" />
          <Typography>
            <b>{props.reportsWithValidationErrorsCount}</b>{" "}
            {pluralize("report", props.reportsWithValidationErrorsCount)}{" "}
            {pluralize("has", props.reportsWithValidationErrorsCount)} validation errors.
          </Typography>
        </Stack>
      )}
    </Dialog>
  );
};
